import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import type {EmployeeLdap, WorkstreamUpdate} from 'client';
import {isLexicalJsonString, toPlainTextLexicalJson} from 'components/DSYS/editor/utils/lexical';
import {RichTextArea} from 'components/DSYS/RichTextArea';
import {format, isSameYear} from 'date-fns';
import type {ReactNode} from 'react';

import {ClampedUpdate} from './ClampedUpdate';
import {PersonLink} from './PersonLink';

export const formatUpdateDate = (date: string) =>
  format(new Date(date), isSameYear(new Date(date), new Date()) ? 'MMM d' : 'MMM d, yyyy');

export const updateValue = (update: WorkstreamUpdate) =>
  isLexicalJsonString(update.comment) ? update.comment : toPlainTextLexicalJson(update.comment);

/** /workstreams: the bordered box an update sits in. */
export const UpdateBox = ({minHeight, children}: {minHeight?: number; children: ReactNode}) => (
  <Box
    borderStyle="Solid"
    borderColor="Border Subtle"
    borderWidth="1"
    borderRadius="Medium"
    padding="16"
    display="flex"
    flexDirection="column"
    style={{flex: 1, minWidth: 0, minHeight}}
  >
    {children}
  </Box>
);

/**
 * /workstreams: one update's byline (the workstream's DRI · when)
 * over its text. The DRI, not whoever submitted it: the Activity log records
 * the submitter. No status: the host already shows it (an open card's header
 * button, the detail page's history dots). Clamped (with a "Show full update"
 * toggle) where it's a preview, like the cards.
 */
export const UpdatePost = ({
  update,
  dri,
  clamped = false,
  lead,
  action,
  when,
}: {
  update: WorkstreamUpdate;
  // Falls back to the submitter when the workstream has no people.
  dri: EmployeeLdap | undefined;
  clamped?: boolean;
  // Between the byline and the update (e.g. an open card's AI summary line).
  lead?: ReactNode;
  // At the byline's end (e.g. the edit pencil).
  action?: ReactNode;
  // In place of the post's date (e.g. a scan card's "6w ago").
  when?: {label: string};
}) => {
  return (
    <Stack gap="8">
      <Box display="flex" alignItems="center" style={{gap: 6, flexWrap: 'wrap'}}>
        <PersonLink person={dri ?? update.author} />
        <Text size="small" color="faint">
          · {when ? when.label : formatUpdateDate(update.created_at)}
        </Text>
        {action}
      </Box>
      {lead}
      {/* Keyed so switching updates remounts the read-only editor. */}
      {clamped ? (
        <ClampedUpdate key={update.id} update={update} />
      ) : (
        <RichTextArea key={update.id} editable={false} value={updateValue(update)} squareBorders />
      )}
    </Stack>
  );
};
